import React, { useEffect, useState } from "react";
import ConsentComponent from "./consentComponent/consentComponent";
import WidgetComponent from "./widgetComponent";
import CodeComponent from "./codeComponent";
import {
  uiConstant,
  failureMessageConstant,
  keyConstants
} from "../../constants";
import Steps from "../../common/components/steps";
import history from "../../managers/history";
import { BusinessConsent, BusinessUser } from "../../services";
import { hideLoader, removeUser, showLoader } from "../../slices/userSlice";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utility";
const {
  setupConsent: { stepDetail, consentData }
} = uiConstant;
const SetupConsentPolicy = () => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);
  const businessID = useSelector((state) => state?.user?._id);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [consentListData, setConsentListData] = useState(consentData);
  const [progress, setProgress] = useState(0);
  const [steps, setSteps] = useState(0);
  const [defaultWidgetSettings, setDefaultWidgetSettings] = useState([]);
  const [selectedWidgetType, setSelectedWidgetType] = useState({
    index: 0,
    value: "FOOTER"
  });
  const [showAdWidget, setShowAdWidget] = useState();
  const [themeColors, setThemeColors] = useState([]);
  const [selectedFont, setSelectedFont] = useState({
    family: "Montserrat",
    fileURL: ""
  });
  const [adUnitCode, setAdUnitCode] = useState("");
  const [isWidgetSettingsChanged, setIsWidgetSettingsChanged] = useState(false);
  const [apiKey, setApiKey] = useState();

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  const getConsentList = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(getConsentList);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      setLoading(true);
      dispatch(showLoader());

      if (
        Utils.localStorageGetItem("consentSetup") &&
        window.location.pathname === "/setup-consent"
      ) {
        history.push("/consent");
      }

      // Call the `getConsentList` function from the `businessConsent` object and pass the `businessId` as an argument
      const consentDataList = await new BusinessConsent().getConsentList(
        businessID
      );

      // Check if the `consentDataList` has any consents
      if (consentDataList?.consentsList[0] !== 0) {
        const updatedConsents = consentDataList?.consentsList.flatMap(
          (consents) =>
            consents.consents.map((element) => ({
              ...element,
              uploadProgress: element?.originalFileName ? 100 : 0
            }))
        );

        // Merge the updated consents with the existing `consentData` array
        const mergedConsents = consentData.flatMap((consent) => {
          const matchedConsent = updatedConsents.find(
            (element) => element.name === consent.name
          );
          if (matchedConsent) {
            return matchedConsent;
          }
          return consent;
        });

        // Update the `consentListData` state with the merged consents
        setConsentListData(mergedConsents);

        setApiKey(consentDataList?.businessData?.apiKey);

        // Set the loading state to false
        setLoading(false);
        dispatch(hideLoader());
        return mergedConsents;
      }
    } catch (error) {
      console.error(error);
      dispatch(hideLoader());
      setLoading(false);
    }
  };

  useEffect(() => {
    if (steps == 0) {
      getConsentList();
    }
  }, [steps]);
  const checkIsActiveConsents = () => {
    const activeConsents = consentListData.filter(
      (consent) => consent.isActive
    );
    return activeConsents.length <= 0;
  };

  const updateWidgetSettings = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      dispatch(showLoader());
      const updatedWidgetSettings = {
        settings: {
          widgetSettings: {
            font: selectedFont,
            type: selectedWidgetType.value,
            theme: {
              backgroundColor: themeColors[0].color,
              fontColor: themeColors[1].color,
              buttonColor: themeColors[2].color
            },
            showAdWidget: showAdWidget,
            adUnitCode: adUnitCode
          }
        }
      };
      await new BusinessUser().updateBusinesssettings(
        businessID,
        updatedWidgetSettings
      );
      Utils.successToastMessage("Widget settings updated successfully");
    } catch (error) {
      Utils.failureToastMessage(error?.response?.data?.message);
    } finally {
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    if (isWidgetSettingsChanged) {
      updateWidgetSettings();
      setIsWidgetSettingsChanged(false);
    }
  }, [isWidgetSettingsChanged]);

  const checkWidgetSettings = async () => {
    const {
      defaultType,
      defaultFont,
      defaultAdUnitCode,
      defaultBackroundColor,
      defaultButtonColor,
      defaultFontColor,
      defaultShowAdWidget
    } = defaultWidgetSettings;

    const widgetSettingsChanged =
      defaultType !== selectedWidgetType.value ||
      defaultFont !== selectedFont ||
      defaultAdUnitCode !== adUnitCode ||
      defaultBackroundColor !== themeColors[0].color ||
      defaultFontColor !== themeColors[1].color ||
      defaultButtonColor !== themeColors[2].color ||
      defaultShowAdWidget !== showAdWidget;
    setIsWidgetSettingsChanged(widgetSettingsChanged);
  };

  const updateSteps = async (type) => {
    if (type !== "back") {
      const isMoreThanOneActive = checkIsActiveConsents();
      if (isMoreThanOneActive) {
        Utils.failureToastMessage(failureMessageConstant.NO_CONSENT_SELECTED);
        return;
      }
      if (steps == 1 && showAdWidget && !adUnitCode) {
        setShowAdWidget(!showAdWidget);
      }
      if (steps === 2) {
        history.push("/consent");
        return;
      }

      setSteps((prevSteps) => {
        const nextSteps = prevSteps + 1;
        return nextSteps;
      });

      setProgress((prevProgress) => {
        const nextProgress = prevProgress + 50;
        return nextProgress;
      });

      if (steps === 1) {
        await checkWidgetSettings();
      }
    } else {
      setProgress((prevProgress) => prevProgress - 50);
      setSteps((prevSteps) => prevSteps - 1);
    }
  };

  const jumpToStep = async (targetStep) => {
    if (targetStep === steps) return; // No action if the current step is clicked

    // Check if there are more than one active consent
    if (checkIsActiveConsents()) {
      Utils.failureToastMessage(failureMessageConstant.NO_CONSENT_SELECTED);
      return;
    }

    // Handle Step 1 specific operations
    if (steps === 1) {
      if (showAdWidget && !adUnitCode) {
        setShowAdWidget(!showAdWidget);
      }
      await checkWidgetSettings();
    }

    // Update steps and progress
    setSteps(targetStep);
    setProgress((targetStep - 1) * 50); // Assuming progress increments by 50 per step
  };

  return (
    <div className=" overflow-auto" style={{ height: window.innerHeight - 65 }}>
      <div className="mx-3 lg:mx-0">
        <div className="mb-6 mt-10 text-center font-PoppinsSemiBold text-ft19-23 md:mb-35px md:text-ft22-33-0.4">
          Setup Consent Policy
        </div>
        <center>
          <div className="relative grid max-w-104 grid-cols-3 place-items-center">
            <div className="absolute top-4.5 -z-10 h-px w-3/4 bg-grey-E0E6EB">
              <hr
                style={{ width: `${progress}%` }}
                className="absolute top-0 z-10 h-px w-full object-cover text-purple-6C36B0"
              />
            </div>
            {stepDetail.map((step, index) => (
              <div
                className="cursor-pointer"
                key={step?.title.toLocaleLowerCase()}
                onClick={() => jumpToStep(index)}
              >
                <Steps
                  step={index}
                  currentStep={steps + 1}
                  title={step?.title}
                />
              </div>
            ))}
          </div>
        </center>
        <div
          className={` ${
            steps == 1 && "mb-40"
          } grid place-items-center  items-center`}
        >
          {(() => {
            switch (steps) {
              case 0:
                return (
                  <ConsentComponent
                    consentListData={consentListData}
                    getConsentList={getConsentList}
                    loading={loading}
                  />
                );
              case 1:
                return (
                  <WidgetComponent
                    setDefaultWidgetSettings={setDefaultWidgetSettings}
                    selectedWidgetType={selectedWidgetType}
                    setSelectedWidgetType={setSelectedWidgetType}
                    themeColors={themeColors}
                    setThemeColors={setThemeColors}
                    selectedFont={selectedFont}
                    setSelectedFont={setSelectedFont}
                    adUnitCode={adUnitCode}
                    setAdUnitCode={setAdUnitCode}
                    showAdWidget={showAdWidget}
                    setShowAdWidget={setShowAdWidget}
                  />
                );
              case 2:
                return (
                  <CodeComponent businessID={businessID} apiKey={apiKey} />
                );
              default:
                return;
            }
          })()}
          <div
            className={`mb-20 mt-5 flex w-full max-w-242 items-center ${
              steps == 0 ? "justify-end" : "justify-between"
            }  gap-2.5`}
          >
            {steps !== 0 && (
              <button
                className="h-12 w-full max-w-183px rounded border border-grey-D9D9D9 bg-white font-PoppinsMedium text-ft15-26.25"
                onClick={() => updateSteps("back")}
              >
                Back
              </button>
            )}

            <button
              className="h-12 w-full max-w-183px rounded bg-purple-6C36B0 font-PoppinsMedium text-ft15-26.25 text-white"
              onClick={() => {
                updateSteps();
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupConsentPolicy;
